import React from "react"

import { Button, Tooltip } from "@coval/ui"

interface DaySelectorProps {
  selectedDays: Set<number>
  onDaysChange: (days: Set<number>) => void
  isDisabled?: boolean
}

const DAYS = [
  { value: 0, label: "S", fullName: "Sunday" },
  { value: 1, label: "M", fullName: "Monday" },
  { value: 2, label: "T", fullName: "Tuesday" },
  { value: 3, label: "W", fullName: "Wednesday" },
  { value: 4, label: "Th", fullName: "Thursday" },
  { value: 5, label: "F", fullName: "Friday" },
  { value: 6, label: "S", fullName: "Saturday" },
]

/**
 * Day selector component for weekly scheduling
 * Allows users to select specific days of the week
 */
export const DaySelector: React.FC<DaySelectorProps> = ({
  selectedDays,
  onDaysChange,
  isDisabled = false,
}) => {
  const handleDayToggle = (day: number) => {
    const newSelectedDays = new Set(selectedDays)

    if (newSelectedDays.has(day)) {
      newSelectedDays.delete(day)
    } else {
      newSelectedDays.add(day)
    }

    onDaysChange(newSelectedDays)
  }

  return (
    <div className="flex gap-2 flex-wrap">
      {DAYS.map((day) => {
        const isSelected = selectedDays.has(day.value)

        return (
          <Tooltip
            key={day.value}
            content={day.fullName}
            triggerDisabled={isDisabled}
          >
            <Button
              variant={isSelected ? "default" : "secondary"}
              onClick={() => handleDayToggle(day.value)}
              disabled={isDisabled}
              className="min-w-[40px] font-medium"
              aria-label={`${isSelected ? "Deselect" : "Select"} ${day.fullName}`}
            >
              {day.label}
            </Button>
          </Tooltip>
        )
      })}
    </div>
  )
}

/**
 * Quick selection presets for common day patterns
 */
interface DayPresetSelectorProps {
  selectedDays: Set<number>
  onDaysChange: (days: Set<number>) => void
  isDisabled?: boolean
}

export const DayPresetSelector: React.FC<DayPresetSelectorProps> = ({
  selectedDays,
  onDaysChange,
  isDisabled = false,
}) => {
  const presets = [
    { label: "Weekdays", days: [1, 2, 3, 4, 5] },
    { label: "Weekends", days: [0, 6] },
    { label: "Every Day", days: [0, 1, 2, 3, 4, 5, 6] },
  ]

  const handlePresetClick = (days: number[]) => {
    onDaysChange(new Set(days))
  }

  const isPresetActive = (presetDays: number[]) => {
    if (selectedDays.size !== presetDays.length) {
      return false
    }
    return presetDays.every((day) => selectedDays.has(day))
  }

  return (
    <div className="flex gap-2 flex-wrap">
      {presets.map((preset) => {
        const isActive = isPresetActive(preset.days)

        return (
          <Button
            key={preset.label}
            variant={isActive ? "secondary" : "ghost"}
            onClick={() => handlePresetClick(preset.days)}
            disabled={isDisabled}
            className="text-xs"
          >
            {preset.label}
          </Button>
        )
      })}
    </div>
  )
}
